// src/components/ActiveSwarmsTabs/ActiveSwarmsTabs.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { 
  selectFilteredVisibleSwarmsByBuyManagers,
  selectSortOption,
  selectSwarmSelected,
  setSwarmIdSelected 
} from "../../../../../store/slices/swarmsInAction";
import { generateTimeSlots } from "../../../utils";
import { Box, Tab, Tabs } from "@mui/material";
import ActiveSwarmList from "./ActiveSwarmList";
import ActiveSwarmsTabsSkeleton from "../skeletons/ActiveSwarmsTabsSkeleton";
import { DROPS_TAB_NAME } from '../constants';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { sortSwarms } from '../../../utils';

export default function ActiveSwarmsTabs() {
  const dispatch = useDispatch();
  const filteredSwarms = useSelector(
    selectFilteredVisibleSwarmsByBuyManagers
  );
  const swarmSelected= useSelector(selectSwarmSelected);
  
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(0);
  const [tabsWithUpdates, setTabsWithUpdates] = useState(new Set());
  const prevSwarmsRef = useRef(null);
  const sortOption = useSelector(selectSortOption);

  // Auto-select time slot and first swarm when data loads, checking localStorage for a saved label
  useEffect(() => {    
    if (filteredSwarms?.length > 0) {
      const timeSlots = generateTimeSlots(filteredSwarms);
      console.log('TABS_EFFECT: Generated timeSlots:', timeSlots);

      // Helper: Get the saved tab index based on the saved label in localStorage
      const getSavedTabIndex = (slots) => {
        const savedTabLabel = localStorage.getItem('activeSwarmsTabLabel');
        if (!savedTabLabel) return -1;
        return slots.findIndex(slot => slot.label === savedTabLabel);
      };

      // Helper: Select the first swarm for a given tab index
      const selectSwarmForSlot = (slotIndex) => {
        setSelectedTimeSlot(slotIndex);
        const sortedSwarmsForTab = sortSwarms(timeSlots[slotIndex].swarms, sortOption);
        if (sortedSwarmsForTab.length > 0 && !swarmSelected) {
          dispatch(setSwarmIdSelected(sortedSwarmsForTab[0].id));
          console.log(`TABS_EFFECT: Auto-selecting swarm for tab at index ${slotIndex}`);
        }
      };

      const savedTabIndex = getSavedTabIndex(timeSlots);
      if (savedTabIndex !== -1) {
        console.log(`TABS_EFFECT: Found saved tab at index ${savedTabIndex}`);
        selectSwarmForSlot(savedTabIndex);
        return; // the tab saved has been found, do not continue with the default logic
      } else if (localStorage.getItem('activeSwarmsTabLabel')) {
        console.log(`TABS_EFFECT: Saved tab label not found in timeSlots. Removing from localStorage.`);
        localStorage.removeItem('activeSwarmsTabLabel');
      }

      // Default logic: auto-select the first tab and its first swarm
      if (timeSlots.length > 0) {
        selectSwarmForSlot(0);
        console.log('TABS_EFFECT: Auto-selecting first tab and its first swarm');
      }
    }
  }, [filteredSwarms, dispatch, sortOption, swarmSelected]);

  // Detect changes in pending/accepted through SignalR
  useEffect(() => {
    console.log('TABS_EFFECT: Checking for updates');
    console.log('TABS_EFFECT: Current filteredSwarms:', filteredSwarms);
    console.log('TABS_EFFECT: Previous filteredSwarms:', prevSwarmsRef.current);

    if (!prevSwarmsRef.current || !filteredSwarms) {
      console.log('TABS_EFFECT: First render or no swarms, setting initial reference');
      prevSwarmsRef.current = filteredSwarms;
      return;
    }

    const updatedTabs = new Set();
    
    filteredSwarms.forEach((swarm, index) => {
      const prevSwarm = prevSwarmsRef.current[index];
      console.log(`TABS_EFFECT: Comparing swarm ${swarm.id}:`, {
        current: { pending: swarm.pending, accepted: swarm.accepted },
        previous: prevSwarm ? { pending: prevSwarm.pending, accepted: prevSwarm.accepted } : 'No previous state'
      });

      // // Only apply effects when the pending count grows
      if (prevSwarm && 
          prevSwarm.pending !== undefined && 
          swarm.pending > prevSwarm.pending) {
          console.log(`TABS_EFFECT: Found update in swarm ${swarm.id}:`, {
          pendingChanged: swarm.pending !== prevSwarm.pending,
          acceptedChanged: swarm.accepted !== prevSwarm.accepted,
          previousPending: prevSwarm.pending,
          currentPending: swarm.pending,
          previousAccepted: prevSwarm.accepted,
          currentAccepted: swarm.accepted
        });

        // Find which tab contains this swarm
        const timeSlots = generateTimeSlots(filteredSwarms);
        timeSlots.forEach((slot, tabIndex) => {
          if (slot.swarms.some(s => s.id === swarm.id)) {
            console.log(`TABS_EFFECT: Adding effect to tab ${tabIndex} (${slot.label})`);
            updatedTabs.add(tabIndex);
          }
        });
      }
    });

    console.log('TABS_EFFECT: Updated tabs:', Array.from(updatedTabs));

    if (updatedTabs.size > 0) {
      console.log('TABS_EFFECT: Setting tab effects');
      setTabsWithUpdates(updatedTabs);
      // Clear effect after timeout
      setTimeout(() => {
        console.log('TABS_EFFECT: Clearing tab effects');
        setTabsWithUpdates(new Set());
      }, 2000);
    }

    prevSwarmsRef.current = filteredSwarms;
  }, [filteredSwarms]);

  // Memoized time slots based on filtered swarms from Redux
  const timeSlots = useMemo(() => {
    console.log("generatedTimeSlots called: ", filteredSwarms);
    if (filteredSwarms && filteredSwarms.length > 0) {
      let generatedTimeSlots = generateTimeSlots(filteredSwarms);
      if (generatedTimeSlots.length === 0) {
        dispatch(setSwarmIdSelected(null));
      }
      return generatedTimeSlots;
    }
    dispatch(setSwarmIdSelected(null));
    return [];
  }, [filteredSwarms]);

  const handleChange = useCallback((event, newValue) => {
    setSelectedTimeSlot(newValue);
    const currentSlot = timeSlots[newValue];
    //saved the tab selection in localStorage
    localStorage.setItem('activeSwarmsTabLabel', currentSlot.label);
  
    // verify the tab exists and has swarms
    if (currentSlot && currentSlot.swarms.length > 0) {
      // sort swarms in the tab according to the selected sort option
      const sortedSwarmsForTab = sortSwarms(currentSlot.swarms, sortOption);
      // select the first swarm from the sorted list
      dispatch(setSwarmIdSelected(sortedSwarmsForTab[0].id));
    } else {
      // if no swarms in the tab, clear the selection
      dispatch(setSwarmIdSelected(null));
    }
  }, [timeSlots, sortOption, dispatch]);

  if (!filteredSwarms) {
    return <ActiveSwarmsTabsSkeleton />;
  }

  if (timeSlots.length === 0) {
    return(
      <h5 style={{marginTop: "20px"}}>There are no active swarms</h5>
    )
  }

  return (
    <div style={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={selectedTimeSlot} 
          onChange={handleChange} 
          aria-label="Active Swarms Time Slots Tabs" 
          variant="scrollable" 
          scrollButtons="auto"
        >
          {timeSlots.map((slot, index) => (
            <Tab 
              key={slot.label}
              label={
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {slot.label}
                  {slot.label === DROPS_TAB_NAME && slot.swarms.some(swarm => swarm.pending > 0) && (
                    <ErrorOutlineIcon style={{ marginLeft: 4, color: 'red' }} />
                  )}
                </span>
              }
              sx={{
                '@keyframes highlight': {
                  '0%': { backgroundColor: 'transparent' },
                  '50%': { backgroundColor: 'rgba(255, 165, 0, 0.3)' },
                  '100%': { backgroundColor: 'transparent' }
                },
                animation: tabsWithUpdates.has(index) 
                  ? 'highlight 2s ease-in-out' 
                  : 'none',
                boxShadow: 'none',
              }}
            />
          ))}
        </Tabs>
      </Box>
      <Box sx={{ padding: '0.5em 0 0 0' }}>
        {selectedTimeSlot < timeSlots.length && (
          <ActiveSwarmList swarms={timeSlots[selectedTimeSlot].swarms} />
        )}
      </Box>
    </div>
  );
}
